import { when } from 'mobx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type {
  INotification,
  IUserNotification,
  Notification as NotificationType,
  UserNotification as UserNotificationType,
} from '@feathr/blackbox';
import { ENotificationLevel } from '@feathr/blackbox';
import { showError } from '@feathr/components';
import { logError, moment } from '@feathr/hooks';

import { useLocalUrl, useStore } from '../state';

export interface IUnifiedNotification
  extends Pick<IUserNotification, 'id'>,
    Pick<INotification, 'level' | 'message' | 'title' | 'timestamp'> {
  isRead: boolean;
  /** Composite function to redirect the user to the Notification link
   * and mark the UserNotification as read. This will not mark as read
   * for critical notifications with specific resolution criteria.
   */
  read: () => void;
}

/**
 * Returns Notification pseudo-models that inherit the necessary data
 * from their corresponding UserNotification parent model.
 */
export default function useUserNotifications(): {
  notifications: IUnifiedNotification[];
  isLoading: boolean;
  readAll: () => Promise<void>;
} {
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { UserNotifications, Notifications } = useStore();
  // UserNotifications are unique and represent the user's read status of the notification.
  const [userNotificationModels, setUserNotificationModels] = useState<UserNotificationType[]>([]);
  // Notifications contain the relevant data corresponding to the event that triggered it.
  // Ex, link to a specific campaign whose budget was hit at a specific time. Title, message, etc.
  // These are not unique to users. Multiple users can see the same Notification.
  const [notificationModels, setNotificationModels] = useState<NotificationType[]>([]);
  const [notifications, setNotifications] = useState<IUnifiedNotification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Helper function to combine UserNotification and Notification into a single pseudo-model.
   */
  function unify({ u, n }: { u: UserNotificationType; n: NotificationType }): IUnifiedNotification {
    const { level, message, timestamp, title } = n.get(['level', 'message', 'timestamp', 'title']);
    return {
      id: u.id,
      isRead: u.get('is_read'),
      level,
      message: n.get('app_message') || message,
      timestamp,
      title: n.get('app_title') || title,
      read: (): void => {
        history.push(localUrl(n.cta));
        // Fire tracking breadcrumb silently if present
        const trackingLink = n.get('action')?.tracking_link;
        if (trackingLink) {
          fetch(trackingLink, { mode: 'no-cors' }).catch(() => {});
        }
        // Attempting to read a critical notification will not mark it as read.
        // Patching "is_read" to true will return an error.
        if (level !== ENotificationLevel.critical) {
          u.read();
        }
      },
    };
  }

  /**
   * Helper function to mark all UserNotifications as read.
   */
  async function markAllAsRead(): Promise<void> {
    // Mark all UserNotifications as read.
    await UserNotifications.readAll();

    // Update the state of the notifications to mark them as read.
    // This is necessary because the UserNotification model does not update
    // the state of the notification models.
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        isRead: true,
      })),
    );
  }

  useEffect(() => {
    async function init(): Promise<void> {
      const uns = UserNotifications.list({
        ordering: ['-timestamp'],
      });
      await when(() => uns.isLoaded);
      setUserNotificationModels(uns.models);

      const ns = Notifications.list({
        filters: { id__in: uns.models.map((un) => un.get('notification_id')) },
        ordering: ['-_id'],
      });
      await when(() => ns.isLoaded);
      setNotificationModels(ns.models);

      // Link UserNotifications to their corresponding Notification.
      const result = userNotificationModels.reduce((acc, u) => {
        const n = notificationModels.find((n) => n.id === u.get('notification_id'));

        // TODO: Implement infinite scroll for Notifications. For now, only show the last 3 months.
        /**
         * To use infinite scroll, we need to create a pseudoclass since managing the logic between
         * the UserNotification and Notification models is complicated and should not live in
         * component files.
         */
        if (n && moment(n.get('timestamp')).isBefore(moment().subtract(3, 'months'))) {
          /**
           * We /could/ filter in the list request to Notifications, but this is safer until
           * we have a pseudoclass to ensure proper error handling.
           */
          return acc;
        } else if (!n) {
          if (process.env.NODE_ENV !== 'production') {
            showError(
              t('Notification {{notif_id}} matching UserNotification {{user_notif_id}} not found', {
                notif_id: u.get('notification_id'),
                user_notif_id: u.id,
              }),
              t,
            );
          }

          // If in Production and the notification is not found, omit the UserNotification
          // and log to sentry
          logError({
            message: `Notification ${u.get('notification_id')} matching UserNotification ${u.id} not found`,
            level: 'warning',
            user: {
              id: u.get('user_id'),
            },
            extra: {
              user_notif_id: u.id,
              notif_id: u.get('notification_id'),
            },
            environment: process.env.NODE_ENV,
          });
          return acc;
        }

        return acc.concat(unify({ u, n }));
      }, [] as IUnifiedNotification[]);

      setNotifications(result);
      setIsLoading(false);
    }

    init();
  }, [UserNotifications, Notifications, notificationModels]);

  return {
    notifications,
    isLoading,
    readAll: async () => markAllAsRead(),
  };
}
